import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@zfp-akademie-a-s/zfp-design";
import { FormCheckbox, FormSwitch, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import {
  type CalculateMonthlyContributionParams,
  calculateMonthlyContribution,
  calculateMonthlyContributionWithFee,
  calculateMonthlyContributionWithoutFee,
  calculateTargetAmount,
} from "Charts/Utils/CommonChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type VehicleGoalFormType, getVehicleGoalFormSchema, minDate } from "Forms/Schemas/Goals/vehicleGoalFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetVehicleGoalQueryKey,
  useCreateVehicleGoal,
  useUpdateVehicleGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type VehicleGoalDto, VehicleGoalDtoPriorityLevel } from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_INFLATION_RATE, INTEREST_RATE_AGGRESIVE, INTEREST_RATE_CONSERVATIVE } from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInMonths, format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModelingAccordion } from "./ModelingAccordion";
import { SavingsChart } from "./SavingsChart";

type Props = {
  readonly onClose: () => void;
  readonly editGoal?: VehicleGoalDto;
  readonly goalId?: string;
};

export const VehicleGoalForm = forwardRef<{}, Props>(({ onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const methods = useForm<VehicleGoalFormType>({
    resolver: yupResolver(getVehicleGoalFormSchema(t)),
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          name: editGoal.name,
          purchaseDate: new Date(editGoal.data.targetDate as string),
          expectedPrice: Number(editGoal.data.vehicleAmount?.value),
          interestRate: Number(editGoal.modeling.appreciationRate),
          includeInflation: editGoal.modeling.inflation?.enabled,
          inflationRate: Number(editGoal.modeling.inflation?.rate),
          includeFee: editGoal.modeling.fee?.enabled,
          feeRate: Number(editGoal.modeling.fee?.rate),
          note: editGoal.note || "",
          priority: editGoal.priorityLevel === VehicleGoalDtoPriorityLevel.high,
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        }
      : {
          includeInflation: true,
          includeFee: true,
          inflationRate: DEFAULT_INFLATION_RATE.toString() as any,
          feeRate: DEFAULT_INFLATION_RATE.toString() as any,
          name: t(Resources.AnalysisDetail.Goals.GoalType.Vehicle),
          referenceStartDate: new Date(),
        },
  });

  const { control, setValue, watch, clearErrors, handleSubmit } = methods;

  const targetDate = watch("purchaseDate");
  const targetAmount = watch("expectedPrice");
  const interestRate = watch("interestRate");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const feeRate = watch("feeRate");
  const inflationRate = watch("inflationRate");
  const monthlyContribution = watch("monthlyContribution");
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const referenceStartDate = watch("referenceStartDate");

  const investmentLength = differenceInMonths(targetDate, new Date());
  const isInvalidPurchaseDate = isBefore(startOfDay(targetDate), startOfDay(minDate));
  const shouldRenderChart =
    targetDate && !isInvalidPurchaseDate && interestRate >= 0 && inflationRate >= 0 && monthlyContribution && targetAmount > 0;

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingMonthlyContributionWithFee, setPendingMonthlyContributionWithFee] = useState<number | null>(null);
  const [lastAutoMonthlyContributionWithFee, setLastAutoMonthlyContributionWithFee] = useState<number | null>(null);

  useEffect(() => {
    if (editGoal) {
      return;
    }

    if (investmentLength && investmentLength > 120) {
      setValue("interestRate", INTEREST_RATE_AGGRESIVE);
    } else {
      setValue("interestRate", INTEREST_RATE_CONSERVATIVE);
    }
  }, [setValue, investmentLength, editGoal]);

  useEffect(() => {
    const durationInMonths = targetDate ? differenceInMonths(targetDate, new Date()) : 0;
    if (includeInflation && inflationRate && interestRate && targetAmount && durationInMonths >= 0) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      setValue("monthlyContribution", monthlyContribution.toString() as any);
    }
  }, [includeInflation, inflationRate, interestRate, targetAmount, setValue, targetDate]);

  useEffect(() => {
    if (!targetDate || isInvalidPurchaseDate) return;
    const durationInMonths = targetDate ? differenceInMonths(targetDate, new Date()) : 0;
    const monthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const newContributionWithFee = calculateMonthlyContributionWithFee(Number(monthlyContribution), Number(includeFee ? feeRate : 0));
    const roundedWithFee = Math.round(newContributionWithFee);
    setValue("monthlyContributionWithFee", roundedWithFee);
    setLastAutoMonthlyContributionWithFee(roundedWithFee);
  }, [feeRate, inflationRate, interestRate, targetAmount, targetDate, isInvalidPurchaseDate, includeInflation, includeFee, setValue]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEditGoal)();
      } else {
        handleSubmit(onCreateGoal)();
      }
    },
  }));

  useEffect(() => {
    if (!targetDate || isInvalidPurchaseDate) return;
    clearErrors("purchaseDate");
    onChangePurchaseDate();
  }, [targetDate, isInvalidPurchaseDate, clearErrors]);

  const onChangePurchaseDate = () => {
    onChange({
      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
      targetAmount,
      inflationRate,
      interestRate,
    });
  };

  const queryClient = useQueryClient();

  const { mutate: createVehicleGoal } = useCreateVehicleGoal();

  const { mutate: updateVehicleGoal } = useUpdateVehicleGoal();

  const onChange = ({ inflationRate, interestRate, targetAmount, durationInMonths }: CalculateMonthlyContributionParams) => {
    if (interestRate && targetAmount && durationInMonths >= 0) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate,
      });
      setValue("monthlyContribution", monthlyContribution.toString() as any);
    }
  };

  const onCreateGoal = (data: VehicleGoalFormType) => {
    createVehicleGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? VehicleGoalDtoPriorityLevel.high : VehicleGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.purchaseDate, "yyyy-MM-dd"),
            vehicleAmount: {
              value: data.expectedPrice,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            investmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: VehicleGoalFormType) => {
    updateVehicleGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? VehicleGoalDtoPriorityLevel.high : VehicleGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.purchaseDate, "yyyy-MM-dd"),
            vehicleAmount: {
              value: data.expectedPrice,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            inflation: {
              enabled: !!includeInflation,
              rate: data.inflationRate,
            },
            investmentAmount: {
              value: includeFee ? data.monthlyContributionWithFee : data.monthlyContribution,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            fee: {
              enabled: !!includeFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetVehicleGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const recalculateMonthlyContributionFromTarget = () => {
    const durationInMonths = targetDate ? differenceInMonths(targetDate, new Date()) : 0;
    const baseMonthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const withFee = calculateMonthlyContributionWithFee(Number(baseMonthlyContribution), Number(includeFee ? feeRate : 0));
    setValue("monthlyContribution", baseMonthlyContribution.toString() as any);
    const roundedWithFee = Math.round(withFee);
    setValue("monthlyContributionWithFee", roundedWithFee);
    setLastAutoMonthlyContributionWithFee(roundedWithFee);
  };

  const handleConfirmResult = (shouldRecalculateTarget: boolean) => {
    if (shouldRecalculateTarget && pendingMonthlyContributionWithFee != null) {
      const durationInMonths = targetDate ? differenceInMonths(targetDate, new Date()) : 0;
      const monthsToYears = durationInMonths / 12;
      const contributionWithoutFee = calculateMonthlyContributionWithoutFee(
        Number(pendingMonthlyContributionWithFee),
        Number(includeFee ? feeRate : 0),
      );

      const newTarget = calculateTargetAmount(
        Number(contributionWithoutFee),
        monthsToYears,
        Number(interestRate) || 0,
        includeInflation ? Number(inflationRate) || 0 : 0,
      );

      setValue("expectedPrice", Math.round(newTarget) as any);
      setValue("monthlyContribution", contributionWithoutFee.toString() as any);
      const roundedWithFee = Math.round(Number(pendingMonthlyContributionWithFee));
      setValue("monthlyContributionWithFee", roundedWithFee as any);
      setLastAutoMonthlyContributionWithFee(roundedWithFee);
    } else {
      recalculateMonthlyContributionFromTarget();
    }

    setIsConfirmOpen(false);
    setPendingMonthlyContributionWithFee(null);
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <ConfirmDialog
            open={isConfirmOpen}
            title={t(Resources.AnalysisDetail.Assets.ModelingModal.Title)}
            message={t(Resources.AnalysisDetail.Goals.ConfirmRecalculateTargetFromMonthly)}
            confirmLabel={t(Resources.Common.Yes)}
            cancelLabel={t(Resources.Common.Cancel)}
            onConfirm={() => handleConfirmResult(true)}
            onCancel={() => handleConfirmResult(false)}
          />
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Vehicle)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormCheckbox
                  control={control}
                  name="priority"
                  label={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  required
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <FormDatePicker
                  control={control}
                  name="purchaseDate"
                  label={t(Resources.AnalysisDetail.Goals.PurchaseDate)}
                  required
                  minDate={startOfMonth(addMonths(new Date(), 1))}
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="expectedPrice"
                  label={t(Resources.AnalysisDetail.Goals.VehicleValue)}
                  required
                  scale={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onBlur={() =>
                    onChange({
                      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                      targetAmount,
                      inflationRate,
                      interestRate,
                    })
                  }
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          {targetDate && targetAmount > 0 && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      required
                      scale={2}
                      thousandsSeparator=" "
                      radix=","
                      mapToRadix={["."]}
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled={readOnly}
                      onBlur={() => {
                        const currentValue = Math.round(Number(monthlyContributionWithFee));
                        if (lastAutoMonthlyContributionWithFee !== null && currentValue === Number(lastAutoMonthlyContributionWithFee)) {
                          return;
                        }
                        setPendingMonthlyContributionWithFee(currentValue);
                        setIsConfirmOpen(true);
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      required
                      scale={2}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={readOnly}
                      onChange={(newInterestRate) => {
                        if (Number(newInterestRate) === Number(inflationRate)) {
                          setValue("interestRate", interestRate);
                          return;
                        }

                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate,
                          interestRate: newInterestRate as any,
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      disabled={readOnly}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        if (newInflationRate === Number(interestRate)) {
                          setValue("inflationRate", newInflationRate + 0.1);
                        } else {
                          setValue("inflationRate", newInflationRate);
                        }

                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate: newInflationRate,
                          interestRate,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="inflationRate"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeInflation || readOnly}
                      onChange={(newInflationRate) => {
                        if (Number(newInflationRate) === Number(interestRate)) {
                          setValue("inflationRate", inflationRate);
                          return;
                        }

                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate: includeInflation ? Number(newInflationRate) : 0,
                          interestRate,
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeFee)}
                      disabled={readOnly}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_INFLATION_RATE : 0;
                        setValue("feeRate", newFeeRate);
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="feeRate"
                      scale={1}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeFee || readOnly}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Grid container spacing={Spacing.XL}>
            {shouldRenderChart && (
              <Grid size={12}>
                <Stack spacing={Spacing.XS} padding={Spacing.S} display="flex" justifyContent="center" alignItems="center">
                  <Typography variant="caption" fontWeight={500}>
                    {t(Resources.Common.Investment)}
                  </Typography>
                  <Typography variant="caption">
                    {formatCurrency(monthlyContributionWithFee, CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                  </Typography>
                </Stack>
                <SavingsChart
                  referenceStartDate={referenceStartDate}
                  targetDate={targetDate}
                  targetAmount={targetAmount}
                  interestRate={interestRate ? Number(interestRate) : 0}
                  monthlyContributionAmount={Number(monthlyContribution)}
                  inflationRate={inflationRate}
                />
              </Grid>
            )}
            <Grid size={12}>
              <FormTextField
                control={control}
                name="note"
                label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                maxLength={NOTE_MAX_LENGTH}
                disabled={readOnly}
                multiline
                minRows={6}
                maxRows={8}
                fullWidth
              />
            </Grid>
          </Grid>
        </Stack>
      </form>
    </FormProvider>
  );
});
